import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { SlidersHorizontal } from 'lucide-react'

import { countryName } from '@/components/CountryFilter'
import { DetailList, DetailRow } from '@/components/DetailList'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
} from '@/components/ui/card'
import type { AdUnit, AdUnitRef } from '@/services/adUnits/adUnitsApi'
import { AuthService } from '@/services/core/auth'
import { SitesApi } from '@/services/sites/sitesApi'
import { formatMoney } from '@/utils/money'

const POLICY_LABELS: Record<string, string> = {
  ALLOWED: 'publishers.policyAllowed',
  REQUIRES_APPROVAL: 'publishers.policyRequiresApproval',
  BLOCKED: 'publishers.policyBlocked',
}

/** `300×250, 728×90`, a `min–max × min–max` range, or nothing when any size goes. */
function sizes(adUnit: AdUnit): string {
  if (adUnit.dimensions.length)
    return adUnit.dimensions.map(([width, height]) => `${width}×${height}`).join(', ')
  const span = (min: number | null, max: number | null) =>
    min || max ? `${min || 0}–${max || '∞'}` : '∞'
  if (!adUnit.minWidth && !adUnit.width && !adUnit.minHeight && !adUnit.height) return ''
  return `${span(adUnit.minWidth, adUnit.width)} × ${span(adUnit.minHeight, adUnit.height)}`
}

/** The values as badges; nothing when there are none, so the row is left out. */
function tags(values: string[]): ReactNode {
  if (!values.length) return null
  return (
    <span className="flex flex-wrap gap-1">
      {values.map((value) => (
        <Badge key={value} variant="secondary">
          {value}
        </Badge>
      ))}
    </span>
  )
}

/** Links to the referenced objects, comma separated; nothing when there are none. */
function refLinks(refs: (AdUnitRef | null | undefined)[], path: string): ReactNode {
  const present = refs.filter((ref): ref is AdUnitRef => !!ref)
  if (!present.length) return null
  return present.map((ref, index) => (
    <span key={ref.id}>
      {index > 0 && ', '}
      <Link to={`${path}/${ref.id}`}>{ref.name || ref.id}</Link>
    </span>
  ))
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      <DetailList>{children}</DetailList>
    </div>
  )
}

export interface AdUnitMatchingCardProps {
  adUnit: AdUnit
}

/**
 * The settings on an ad unit and its site that decide which line items can
 * compete on it — sizes, labels, rulesets, how the page is read — so a
 * diagnostic result can be read against them.
 */
export function AdUnitMatchingCard({ adUnit }: AdUnitMatchingCardProps) {
  const { t } = useTranslation()
  const siteId = adUnit.site?.id
  // Shares the site page's query, so a site opened before costs nothing.
  const { data: site } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => SitesApi.getById(siteId!),
    enabled: !!siteId && AuthService.isAuthenticated(),
    staleTime: 5 * 60 * 1000,
  })

  const policy = (value: string | undefined) =>
    value && POLICY_LABELS[value] ? t(POLICY_LABELS[value]) : undefined
  const pageReading = [
    adUnit.urlAsCategory && t('diagnostics.adUnitInfo.urlAsCategory'),
    adUnit.qsAsKeyValues && t('diagnostics.adUnitInfo.qsAsKeyValues'),
  ].filter((value): value is string => !!value)

  return (
    <Card tone="insight">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <CardIcon icon={SlidersHorizontal} />
          {t('diagnostics.adUnitInfo.title')}
        </CardTitle>
        <CardDescription>{t('diagnostics.adUnitInfo.description')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-2">
        <Section title={t('objectTypes.adUnit')}>
          <DetailRow label={t('diagnostics.adUnitInfo.sizes')}>
            {sizes(adUnit) || t('diagnostics.adUnitInfo.anySize')}
            {adUnit.isFullPage && ` · ${t('diagnostics.adUnitInfo.fullPage')}`}
          </DetailRow>
          <DetailRow label={t('adUnits.matchingLabels')}>{tags(adUnit.matchingLabels)}</DetailRow>
          <DetailRow label={t('publishers.labels')}>{tags(adUnit.labels)}</DetailRow>
          <DetailRow label={t('diagnostics.adUnitInfo.targetingControl')}>
            {adUnit.targetingControl === 'ONLY_TARGETING'
              ? t('diagnostics.adUnitInfo.onlyTargeting')
              : adUnit.targetingControl === 'ONLY_SELECTED_TARGETING' && (
                  <span className="flex flex-col gap-1">
                    {t('diagnostics.adUnitInfo.onlySelectedTargeting')}
                    {tags(adUnit.allowedTargetTypes)}
                  </span>
                )}
          </DetailRow>
          <DetailRow label={t('diagnostics.adUnitInfo.pageReading')}>
            {pageReading.join(' · ')}
          </DetailRow>
          <DetailRow label={t('diagnostics.adUnitInfo.floorPrice')}>
            {adUnit.floorPrice && formatMoney(adUnit.floorPrice.amount, adUnit.floorPrice.currency)}
          </DetailRow>
          <DetailRow label={t('publishers.blocklist')}>
            {refLinks([adUnit.blocklist], '/publishing/blocklists')}
          </DetailRow>
          <DetailRow label={t('publishers.inventoryRuleset')}>
            {refLinks([adUnit.siteRuleset], '/publishing/rulesets')}
          </DetailRow>
          <DetailRow label={t('publishers.targetingRulesets')}>
            {refLinks(adUnit.targetingRulesets, '/publishing/rulesets')}
          </DetailRow>
          <DetailRow label={t('publishers.sizeRulesets')}>
            {refLinks(adUnit.sizeRulesets, '/publishing/rulesets')}
          </DetailRow>
          <DetailRow label={t('sites.politicalAds')}>
            {policy(adUnit.politicalAdvertising)}
          </DetailRow>
        </Section>

        {adUnit.site && (
          <Section title={t('objectTypes.site')}>
            <DetailRow label={t('objectTypes.site')}>
              {refLinks([adUnit.site], '/publishing/sites')}
            </DetailRow>
            <DetailRow label={t('objectTypes.earningsAccount')}>
              {refLinks(
                [
                  site?.earningsAccountId
                    ? { id: site.earningsAccountId, name: site.earningsAccountName }
                    : null,
                ],
                '/publishing/earnings'
              )}
            </DetailRow>
            <DetailRow label={t('common.country')}>
              {site?.country && countryName(site.country)}
            </DetailRow>
            <DetailRow label={t('sites.siteGroups')}>
              {refLinks(site?.siteGroups ?? [], '/publishing/site-groups')}
            </DetailRow>
            <DetailRow label={t('publishers.labels')}>{tags(site?.labels ?? [])}</DetailRow>
            <DetailRow label={t('sites.paywalledContent')}>
              {site?.paywalled && t('common.yes')}
            </DetailRow>
            <DetailRow label={t('publishers.inventoryRuleset')}>
              {refLinks([site?.siteRuleset], '/publishing/rulesets')}
            </DetailRow>
            <DetailRow label={t('publishers.targetingRulesets')}>
              {refLinks(site?.targetingRulesets ?? [], '/publishing/rulesets')}
            </DetailRow>
            <DetailRow label={t('publishers.sizeRulesets')}>
              {refLinks(site?.sizeRulesets ?? [], '/publishing/rulesets')}
            </DetailRow>
            <DetailRow label={t('sites.politicalAds')}>
              {policy(site?.politicalAdvertising)}
            </DetailRow>
          </Section>
        )}
      </CardContent>
    </Card>
  )
}
